import { apiRequest } from './client';
import type {
  ActiveCollectionRow,
  AdminLoginResponse,
  CardLookupDto,
  InsightsOverviewDto,
  PriceAlertSeriesPoint,
  PriceAlertTopCardRow,
  PriceAlertsOverviewDto,
  PricingHealthDto,
  ScanCaptureConfig,
  ScanSessionDetail,
  ScanSessionRow,
  ScannerFeedbackDto,
  ScannerOverviewDto,
  SubscriptionsOverviewDto,
  SubscriptionsSeriesDto,
  TopCardRow,
  User360Dto,
  UserSearchRow,
  UserSeriesPoint,
} from './types';

export const insightsApi = {
  // ---------- auth (shared staff login with the admin panel) ----------
  login(username: string, password: string): Promise<AdminLoginResponse> {
    return apiRequest('/admin/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  },

  // ---------- overview ----------
  overview(): Promise<InsightsOverviewDto> {
    return apiRequest('/admin/insights/overview');
  },

  usersSeries(months = 12): Promise<UserSeriesPoint[]> {
    return apiRequest('/admin/insights/users/series', { query: { months } });
  },

  topCards(limit = 25): Promise<TopCardRow[]> {
    return apiRequest('/admin/insights/collections/top-cards', { query: { limit } });
  },

  activeCollections(limit = 25): Promise<ActiveCollectionRow[]> {
    return apiRequest('/admin/insights/collections/active', { query: { limit } });
  },

  recentUsers(limit = 50): Promise<UserSearchRow[]> {
    return apiRequest('/admin/insights/users/recent', { query: { limit } });
  },

  // ---------- users ----------
  searchUsers(q: string, limit = 25): Promise<UserSearchRow[]> {
    return apiRequest('/admin/insights/users/search', { query: { q, limit } });
  },

  user(id: number | string): Promise<User360Dto> {
    return apiRequest(`/admin/insights/users/${id}`);
  },

  // ---------- subscriptions & revenue ----------
  subscriptionsOverview(): Promise<SubscriptionsOverviewDto> {
    return apiRequest('/admin/insights/subscriptions/overview');
  },

  subscriptionsSeries(days = 30, granularity = 'day'): Promise<SubscriptionsSeriesDto> {
    return apiRequest('/admin/insights/subscriptions/series', { query: { days, granularity } });
  },

  // ---------- scanner health ----------
  scannerOverview(version?: string): Promise<ScannerOverviewDto> {
    return apiRequest('/admin/insights/scanner/overview', { query: { version } });
  },

  scannerFeedback(limit = 50): Promise<ScannerFeedbackDto> {
    return apiRequest('/admin/insights/scanner/feedback', { query: { limit } });
  },

  // ---------- pricing engine health ----------
  pricingHealth(sample = 25): Promise<PricingHealthDto> {
    return apiRequest('/admin/insights/pricing/health', { query: { sample } });
  },

  // ---------- price alerts ----------
  priceAlertsOverview(): Promise<PriceAlertsOverviewDto> {
    return apiRequest('/admin/insights/price-alerts/overview');
  },

  priceAlertsTopCards(limit = 25): Promise<PriceAlertTopCardRow[]> {
    return apiRequest('/admin/insights/price-alerts/top-cards', { query: { limit } });
  },

  priceAlertsSeries(days = 30): Promise<PriceAlertSeriesPoint[]> {
    return apiRequest('/admin/insights/price-alerts/series', { query: { days } });
  },

  // ---------- scan sessions (scanner diagnostics) ----------
  scanSessions(params: { outcome?: string; appVersion?: string; limit?: number } = {}): Promise<{ items: ScanSessionRow[] }> {
    return apiRequest('/admin/scan-session', { query: params });
  },

  scanSession(id: number): Promise<ScanSessionDetail> {
    return apiRequest(`/admin/scan-session/${id}`);
  },

  scanSessionConfig(): Promise<ScanCaptureConfig> {
    return apiRequest('/admin/scan-session/config');
  },

  updateScanSessionConfig(patch: Partial<ScanCaptureConfig>): Promise<ScanCaptureConfig> {
    return apiRequest('/admin/scan-session/config', { method: 'PUT', body: patch });
  },

  // ---------- public card lookup (name + art + headline price) ----------
  // Hits the app-facing `GET /v1/cards/:id`; used to reconstruct the scanner
  // reveal overlay from a session's telemetry `cardId`s.
  card(id: string): Promise<CardLookupDto> {
    return apiRequest(`/cards/${encodeURIComponent(id)}`);
  },
};
